/**
 * Schema.org Structured Data Utilities
 * 
 * Provides functions to generate JSON-LD structured data for SEO
 */

import { COMPANY } from '@/lib/companyInfo';

export interface OrganizationSchema {
  '@context': string;
  '@type': 'Organization';
  name: string;
  legalName?: string;
  url: string;
  logo?: string;
  description?: string;
  sameAs?: string[];
  contactPoint?: {
    '@type': 'ContactPoint';
    contactType: string;
    email?: string;
    url?: string;
  };
}

export interface BreadcrumbSchema {
  '@context': string;
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item?: string;
  }>;
}

export interface OccupationSchema {
  '@context': string;
  '@type': 'Occupation';
  name: string;
  description?: string;
  occupationLocation?: {
    '@type': 'AdministrativeArea';
    name: string;
  };
  estimatedSalary?: Array<{
    '@type': 'MonetaryAmountDistribution';
    name: string;
    currency: string;
    duration: string;
    median?: number;
    mean?: number;
    minValue?: number;
    maxValue?: number;
    percentile10?: number;
    percentile25?: number;
    percentile75?: number;
    percentile90?: number;
  }>;
  dateModified?: string;
}

export interface WebPageSchema {
  '@context': string;
  '@type': 'WebPage';
  '@id': string;
  name: string;
  description: string;
  url: string;
  dateModified?: string;
  publisher?: OrganizationSchema;
  mainEntity?: OccupationSchema;
  breadcrumb?: BreadcrumbSchema;
}

/**
 * Generate Organization schema
 */
export function generateOrganizationSchema(baseUrl: string = 'https://wageatlas.com'): OrganizationSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: COMPANY.brandName,
    legalName: COMPANY.legalName,
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description: 'Professional salary research platform providing comprehensive wage data and career insights.',
    sameAs: [
      // Add social media links when available
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'support@wageatlas.com',
      url: `${baseUrl}/contact`,
    },
  };
}

/**
 * Generate BreadcrumbList schema
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url?: string }>,
  baseUrl: string = 'https://wageatlas.com'
): BreadcrumbSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url || (index === items.length - 1 ? undefined : `${baseUrl}${item.url || ''}`),
    })),
  };
}

/**
 * Generate Occupation schema with salary data
 */
export function generateOccupationSchema(
  occupation: string,
  location?: string,
  salaryData?: {
    median: number;
    mean: number;
    min: number;
    max: number;
    p10?: number;
    p25?: number;
    p75?: number;
    p90?: number;
    currency?: string;
  }
): OccupationSchema {
  const schema: OccupationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: occupation,
    dateModified: new Date().toISOString().split('T')[0],
  };

  if (location) {
    schema.occupationLocation = {
      '@type': 'AdministrativeArea',
      name: location,
    };
  }

  if (salaryData) {
    schema.estimatedSalary = [
      {
        '@type': 'MonetaryAmountDistribution',
        name: 'Annual Salary Distribution',
        currency: salaryData.currency || 'USD',
        duration: 'P1Y',
        median: salaryData.median,
        mean: salaryData.mean,
        minValue: salaryData.min,
        maxValue: salaryData.max,
        percentile10: salaryData.p10,
        percentile25: salaryData.p25,
        percentile75: salaryData.p75,
        percentile90: salaryData.p90,
      },
    ];
  }

  return schema;
}

/**
 * Generate comprehensive WebPage schema
 */
export function generateWebPageSchema(
  title: string,
  description: string,
  url: string,
  options?: {
    occupation?: string;
    location?: string;
    salaryData?: {
      median: number;
      mean: number;
      min: number;
      max: number;
      p10?: number;
      p25?: number;
      p75?: number;
      p90?: number;
      currency?: string;
    };
    breadcrumbs?: Array<{ name: string; url?: string }>;
    baseUrl?: string;
  }
): WebPageSchema {
  const baseUrl = options?.baseUrl || 'https://wageatlas.com';
  
  const schema: WebPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': url,
    name: title,
    description: description,
    url: url,
    dateModified: new Date().toISOString().split('T')[0],
    publisher: generateOrganizationSchema(baseUrl),
  };

  // Add breadcrumbs if provided
  if (options?.breadcrumbs && options.breadcrumbs.length > 0) {
    schema.breadcrumb = generateBreadcrumbSchema(options.breadcrumbs, baseUrl);
  }

  // Add occupation schema if provided
  if (options?.occupation) {
    schema.mainEntity = generateOccupationSchema(
      options.occupation,
      options.location,
      options.salaryData
    );
  }

  return schema;
}

/**
 * Convert schema object to JSON-LD string
 */
export function schemaToJsonLd(schema: unknown): string {
  return JSON.stringify(schema, null, 2);
}

/**
 * Generate multiple schemas as an array
 */
export function generateMultipleSchemas(schemas: unknown[]): string {
  return JSON.stringify(schemas, null, 2);
}

/**
 * FAQPage Schema Interface
 */
export interface FAQPageSchema {
  '@context': string;
  '@type': 'FAQPage';
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

/**
 * Generate FAQPage schema
 */
export function generateFAQPageSchema(
  faqs: Array<{ question: string; answer: string }>
): FAQPageSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * AboutPage Schema Interface
 */
export interface AboutPageSchema {
  '@context': string;
  '@type': 'AboutPage';
  '@id': string;
  name: string;
  description: string;
  url: string;
  publisher?: OrganizationSchema;
  breadcrumb?: BreadcrumbSchema;
}

/**
 * Generate AboutPage schema
 */
export function generateAboutPageSchema(
  title: string,
  description: string,
  url: string,
  baseUrl: string = 'https://wageatlas.com',
  breadcrumbs?: Array<{ name: string; url?: string }>
): AboutPageSchema {
  const schema: AboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': url,
    name: title,
    description: description,
    url: url,
    publisher: generateOrganizationSchema(baseUrl),
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    schema.breadcrumb = generateBreadcrumbSchema(breadcrumbs, baseUrl);
  }

  return schema;
}

/**
 * ContactPage Schema Interface
 */
export interface ContactPageSchema {
  '@context': string;
  '@type': 'ContactPage';
  '@id': string;
  name: string;
  description: string;
  url: string;
  publisher?: OrganizationSchema;
  breadcrumb?: BreadcrumbSchema;
}

/**
 * Generate ContactPage schema
 */
export function generateContactPageSchema(
  title: string,
  description: string,
  url: string,
  baseUrl: string = 'https://wageatlas.com',
  breadcrumbs?: Array<{ name: string; url?: string }>
): ContactPageSchema {
  const schema: ContactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': url,
    name: title,
    description: description,
    url: url,
    publisher: generateOrganizationSchema(baseUrl),
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    schema.breadcrumb = generateBreadcrumbSchema(breadcrumbs, baseUrl);
  }

  return schema;
}

/**
 * Article Schema Interface
 */
export interface ArticleSchema {
  '@context': string;
  '@type': 'Article';
  headline: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  author?: {
    '@type': 'Organization';
    name: string;
  };
  publisher?: OrganizationSchema;
}

/**
 * Generate Article schema for blog posts/insights
 */
export function generateArticleSchema(
  headline: string,
  description: string,
  url: string,
  baseUrl: string = 'https://wageatlas.com',
  datePublished?: string,
  dateModified?: string
): ArticleSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: headline,
    description: description,
    url: url,
    datePublished: datePublished || new Date().toISOString().split('T')[0],
    dateModified: dateModified || new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'Wage Atlas',
    },
    publisher: generateOrganizationSchema(baseUrl),
  };
}

/**
 * Service Schema Interface
 */
export interface ServiceSchema {
  '@context': string;
  '@type': 'Service';
  name: string;
  description: string;
  provider: {
    '@type': 'Organization';
    name: string;
    url?: string;
  };
  offers?: {
    '@type': 'Offer';
    price: string;
    priceCurrency: string;
    availability?: string;
    url?: string;
  };
  areaServed?: {
    '@type': 'Country';
    name: string;
  };
}

/**
 * Generate Service schema
 */
export function generateServiceSchema(
  name: string,
  description: string,
  baseUrl: string = 'https://wageatlas.com',
  price?: string,
  priceCurrency: string = 'USD',
  url?: string
): ServiceSchema {
  const schema: ServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: name,
    description: description,
    provider: {
      '@type': 'Organization',
      name: 'Wage Atlas',
      url: baseUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
  };

  if (price) {
    schema.offers = {
      '@type': 'Offer',
      price: price,
      priceCurrency: priceCurrency,
      availability: 'https://schema.org/InStock',
      url: url || `${baseUrl}/ai-career-analysis`,
    };
  }

  return schema;
}

/**
 * VideoObject Schema Interface
 */
export interface VideoObjectSchema {
  '@context': string;
  '@type': 'VideoObject';
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string; // ISO 8601 duration (e.g., "PT5M30S" for 5:30)
  contentUrl?: string; // Direct video URL
  embedUrl?: string; // Embed URL (for Vimeo: https://player.vimeo.com/video/VIDEO_ID)
  publisher?: OrganizationSchema;
}

/**
 * Dataset Schema Interface
 */
export interface DatasetSchema {
  '@context': string;
  '@type': 'Dataset';
  name: string;
  description: string;
  keywords?: string[];
  creator: OrganizationSchema;
  license: string;
  about?: {
    '@type': 'Occupation';
    name: string;
    occupationalCategory?: string;
  };
}

/**
 * Generate VideoObject schema for Vimeo videos
 */
export function generateVideoObjectSchema(
  name: string,
  description: string,
  vimeoVideoId: string,
  uploadDate: string,
  baseUrl: string = 'https://wageatlas.com',
  options?: {
    duration?: string; // ISO 8601 format: "PT5M30S" = 5 minutes 30 seconds
    thumbnailUrl?: string; // Optional custom thumbnail, defaults to Vimeo thumbnail
  }
): VideoObjectSchema {
  // Vimeo embed URL format
  const embedUrl = `https://player.vimeo.com/video/${vimeoVideoId}`;
  
  // Vimeo content URL format
  const contentUrl = `https://vimeo.com/${vimeoVideoId}`;
  
  // Vimeo thumbnail URL (you can also use Vimeo API to get custom thumbnails)
  const thumbnailUrl = options?.thumbnailUrl || `https://vumbnail.com/${vimeoVideoId}.jpg`;

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: name,
    description: description,
    thumbnailUrl: thumbnailUrl,
    uploadDate: uploadDate,
    duration: options?.duration,
    contentUrl: contentUrl,
    embedUrl: embedUrl,
    publisher: generateOrganizationSchema(baseUrl),
  };
}

/**
 * Generate Dataset schema for geographic salary analysis
 */
export function generateDatasetSchema(
  name: string,
  description: string,
  baseUrl: string = 'https://wageatlas.com',
  options?: {
    keywords?: string[];
    about?: {
      name: string;
      occupationalCategory?: string;
    };
    license?: string;
  }
): DatasetSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: name,
    description: description,
    keywords: options?.keywords,
    creator: generateOrganizationSchema(baseUrl),
    license: options?.license || 'https://creativecommons.org/licenses/by/4.0/',
    about: options?.about ? {
      '@type': 'Occupation',
      name: options.about.name,
      occupationalCategory: options.about.occupationalCategory,
    } : undefined,
  };
}
